// Product info + quick expense entry. Shows full product details from OpenFoodFacts
// + AI-based price hint (last purchase or estimated retail price).
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, History, X } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useAccounts } from "@/lib/useAccounts";
import { ProductDetails } from "@/components/add/ProductDetails";
import { PriceComparison } from "@/components/add/PriceComparison";
import { localToday } from "@/lib/localDate";

export function QrQuickAmount({ open, code, product, onSaved, onClose }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { items: accounts = [] } = useAccounts();
  const inputRef = useRef(null);
  const [amount, setAmount] = useState("");
  const [accId, setAccId] = useState("");
  const [hint, setHint] = useState(null); // { last?, market?, estimate? }
  const [loadingHint, setLoadingHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const AR = lang === "ar";
  const ccy = user?.currency || "AED";

  useEffect(() => { if (open && accounts.length === 1) setAccId(accounts[0].id); }, [open, accounts]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 100); }, [open]);
  useEffect(() => {
    if (!open || !code) return;
    setHint(null); setAmount(""); setLoadingHint(true);
    api.post("/products/price-hint", { barcode: code, name: product?.name, brand: product?.brand, quantity: product?.quantity })
      .then(({ data }) => {
        setHint(data);
        const pre = data?.last?.amount ?? data?.market?.prices?.[0]?.price ?? data?.estimate?.typical;
        if (pre) setAmount(String(pre));
      }).catch(() => {}).finally(() => setLoadingHint(false));
  }, [open, code, product?.name, product?.brand]);
  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    const a = parseFloat(amount);
    if (!(a > 0)) { toast.error(AR ? "أدخل السعر" : "Enter price"); return; }
    if (!accId) { toast.error(AR ? "اختر حساب" : "Select account"); return; }
    setBusy(true);
    try {
      await api.post("/transactions", {
        type: "expense", amount: a, currency: ccy, category: product?.category || "groceries",
        description: `${product?.name || `Product ${code}`} [barcode:${code}]`,
        date: localToday(), account_id: accId, force: true,
      });
      toast.success(t("saved")); onSaved?.();
    } catch { toast.error(t("error")); } finally { setBusy(false); }
  };

  const hasMarket = !!hint?.market?.prices?.length;
  const hintNode = (
    <>
      {loadingHint && (
        <p className="text-[11px] text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{AR ? "جاري البحث عن أفضل الأسعار…" : "Searching best prices…"}</p>
      )}
      {hint?.last && (
        <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1"><History className="w-3 h-3" />{AR ? "آخر شراء لك: " : "Your last: "}{hint.last.amount} {hint.last.currency} · {hint.last.date}</p>
      )}
      {hasMarket ? (
        <PriceComparison market={hint.market} onPick={(p) => setAmount(String(p))} ar={AR} />
      ) : hint?.estimate ? (
        <p className="text-[11px] text-blue-700 font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" />{AR ? "تقدير AI: " : "AI estimate: "}{hint.estimate.low}–{hint.estimate.high} {hint.estimate.currency}</p>
      ) : null}
    </>
  );

  return (
    <div data-testid="qr-quick-amount" dir={AR ? "rtl" : "ltr"} className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-md flex items-end justify-center" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg h-[75vh] bg-card rounded-t-3xl shadow-2xl flex flex-col animate-in slide-in-from-bottom-full duration-300">
        <button type="button" data-testid="qr-close" aria-label={AR ? "إغلاق" : "close"} onClick={onClose} className="absolute top-3 end-3 w-9 h-9 rounded-full bg-white hover:bg-red-50 grid place-items-center z-10 active:scale-95 shadow-md"><X className="w-5 h-5 text-slate-600" /></button>
        <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4 space-y-4">
          <ProductDetails p={product} code={code} />
          <div className="space-y-1">
            <input data-testid="qr-price-input" ref={inputRef} type="number" step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={AR ? `السعر (${ccy})` : `Price (${ccy})`} className="w-full px-4 py-4 rounded-2xl neu-inset text-lg font-bold tabular-nums text-center" />
            {hintNode}
          </div>
          <select data-testid="qr-account" value={accId} onChange={(e) => setAccId(e.target.value)} className="w-full px-4 py-3 rounded-2xl neu-inset text-sm font-semibold">
            <option value="">{AR ? "— اختر حساب الدفع" : "— Pay from"}</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.currency}</option>)}
          </select>
        </div>
        <div className="px-5 pb-5 pt-3 border-t border-slate-200 shrink-0 bg-card">
          <button data-testid="qr-save" type="submit" disabled={busy || !amount || !accId} className="w-full py-3 rounded-2xl neu-fill-blue text-white font-semibold shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy ? "…" : (AR ? "احفظ" : "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}
